import { getApiBaseUrl } from "../config/api.js";
import { composeAbortSignals, sleep } from "./abort.js";
import {
	ApiError,
	isAbortError,
	NetworkError,
	parseErrorEnvelope,
	parseRetryAfter,
} from "./error.js";
import type {
	FloorPlan,
	Hotspot,
	HotspotMedia,
	ModelManifest,
	ModelMeta,
	ModelMetaVariant,
	Preset,
} from "./types.js";
import { isSafeHttpUrl } from "./url-safety.js";

export interface ApiClientConfig {
	baseUrl?: string;
	fetch?: typeof globalThis.fetch;
	maxAttempts?: number;
	baseBackoffMs?: number;
	maxBackoffMs?: number;
	signal?: AbortSignal;
	/**
	 * Audit C3 fix 2026-06-03 (CRITICAL): share_token per accesso a model
	 * share_only. Quando il viewer e' in modalita' share (URL contiene il
	 * token recuperato da /api/share/:token), tutte le chiamate ai 4
	 * endpoint pubblici (/api/model/:id, /hotspots, /presets, /floorplan)
	 * devono includere `?share_token=<token>` per superare il gate
	 * `assertPublicModelAccessWithShareToken` nel backend.
	 * Quando undefined, le chiamate sono "public-only" (no share_only).
	 */
	shareToken?: string;
}

export interface ApiCallOptions {
	signal?: AbortSignal;
}

// Step 7 Phase 2 — fetchModel optionally accepts a `variant` to use the
// `?variant=hd|sd|auto` flow. Without `variant` the client gets the
// LEGACY shape (ModelMeta byte-identical). With `variant` the response
// is extended with `variant_served` + `variant_warning` fields
// (ModelMetaVariant). Phase 6 wires this for adaptive request from main.ts.
export interface FetchModelOptions extends ApiCallOptions {
	variant?: "hd" | "sd" | "auto" | "rad";
}

export interface ApiClient {
	fetchModel(
		modelId: string,
		opts?: FetchModelOptions,
	): Promise<ModelMeta | ModelMetaVariant>;
	fetchModelManifest(
		modelId: string,
		opts?: ApiCallOptions,
	): Promise<ModelManifest>;
	fetchHotspots(
		modelId: string,
		opts?: ApiCallOptions,
	): Promise<readonly Hotspot[]>;
	fetchPresets(
		modelId: string,
		opts?: ApiCallOptions,
	): Promise<readonly Preset[]>;
	fetchFloorPlan(
		modelId: string,
		opts?: ApiCallOptions,
	): Promise<FloorPlan | null>;
}

interface ResolvedConfig {
	baseUrl: string;
	fetchFn: typeof globalThis.fetch;
	maxAttempts: number;
	baseBackoffMs: number;
	maxBackoffMs: number;
	clientSignal: AbortSignal | undefined;
	shareToken: string | undefined;
}

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_BASE_BACKOFF_MS = 250;
const DEFAULT_MAX_BACKOFF_MS = 5000;

function resolveConfig(config: ApiClientConfig | undefined): ResolvedConfig {
	return {
		baseUrl: (config?.baseUrl ?? getApiBaseUrl()).replace(/\/+$/, ""),
		fetchFn: config?.fetch ?? globalThis.fetch.bind(globalThis),
		maxAttempts: config?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
		baseBackoffMs: config?.baseBackoffMs ?? DEFAULT_BASE_BACKOFF_MS,
		maxBackoffMs: config?.maxBackoffMs ?? DEFAULT_MAX_BACKOFF_MS,
		clientSignal: config?.signal,
		shareToken: config?.shareToken,
	};
}

/**
 * Audit C3 fix 2026-06-03: appende `share_token=<token>` come query param a
 * un path che puo' gia' avere un `?variant=`. Idempotente: se gia' presente,
 * non duplica.
 */
function appendShareToken(path: string, token: string | undefined): string {
	if (token === undefined || token.length === 0) return path;
	if (path.includes("share_token=")) return path;
	const sep = path.includes("?") ? "&" : "?";
	return `${path}${sep}share_token=${encodeURIComponent(token)}`;
}

function backoffDelay(baseMs: number, maxMs: number, attempt: number): number {
	// Full jitter exponential: random in [0, base * 2^attempt], cap maxMs.
	const exp = baseMs * 2 ** attempt;
	return Math.min(maxMs, Math.floor(Math.random() * exp));
}

interface FailureClassification {
	shouldRetry: boolean;
	delayMs: number | null; // null → use backoff
	error: Error;
}

async function classifyResponse(
	response: Response,
	resolved: ResolvedConfig,
	attempt: number,
): Promise<
	| { ok: true; payload: unknown }
	| { ok: false; classification: FailureClassification }
> {
	let payload: unknown = null;
	try {
		payload = await response.json();
	} catch {
		// Non-JSON 5xx is still retryable; 2xx without JSON is invalid.
		if (response.ok) {
			return {
				ok: false,
				classification: {
					shouldRetry: false,
					delayMs: null,
					error: new ApiError({
						code: "internal_error",
						message: "Malformed JSON response",
						httpStatus: response.status,
						requestId: response.headers.get("X-Request-Id") ?? "",
						// Override: 2xx con JSON rotto è terminal — il server ha risposto OK
						// ma il payload è corrotto, retry non aiuta. NON usare il default
						// retryable derived from internal_error code.
						retryable: false,
					}),
				},
			};
		}
	}

	if (response.ok) {
		return { ok: true, payload };
	}

	const envelope = parseErrorEnvelope(payload);
	const requestId =
		envelope?.error.request_id ?? response.headers.get("X-Request-Id") ?? "";
	const code = envelope?.error.code;
	const message =
		envelope?.error.message ?? `HTTP ${response.status} (no error envelope)`;
	const details = envelope?.error.details;
	const retryAfterMs = parseRetryAfter(response.headers.get("Retry-After"));

	// Network/transport-level retryable: 5xx (no envelope) and 429 (rate_limited)
	const isHttp5xx = response.status >= 500 && response.status < 600;
	const isRateLimited = code === "rate_limited" || response.status === 429;
	const shouldRetry =
		(isHttp5xx || isRateLimited) && attempt + 1 < resolved.maxAttempts;

	const apiError = new ApiError({
		code: code ?? (isHttp5xx ? "internal_error" : "invalid_payload"),
		message,
		httpStatus: response.status,
		requestId,
		...(details !== undefined && { details }),
		retryAfterMs,
	});

	return {
		ok: false,
		classification: {
			shouldRetry,
			delayMs: retryAfterMs,
			error: apiError,
		},
	};
}

async function performWithRetry(
	url: string,
	resolved: ResolvedConfig,
	callSignal: AbortSignal | undefined,
): Promise<unknown> {
	const composed = composeAbortSignals(resolved.clientSignal, callSignal);
	const { signal: composedSignal } = composed;

	try {
		for (let attempt = 0; attempt < resolved.maxAttempts; attempt++) {
			let response: Response;
			try {
				response = await resolved.fetchFn(url, {
					method: "GET",
					headers: { Accept: "application/json" },
					signal: composedSignal,
					mode: "cors",
					credentials: "omit",
					cache: "no-store",
				});
			} catch (err) {
				if (isAbortError(err)) throw err;
				const isLastAttempt = attempt + 1 >= resolved.maxAttempts;
				if (isLastAttempt) {
					throw new NetworkError(
						err instanceof Error ? err.message : String(err),
						err,
					);
				}
				await sleep(
					backoffDelay(resolved.baseBackoffMs, resolved.maxBackoffMs, attempt),
					composedSignal,
				);
				continue;
			}

			const classified = await classifyResponse(response, resolved, attempt);
			if (classified.ok) return classified.payload;

			const { shouldRetry, delayMs, error } = classified.classification;
			if (!shouldRetry) throw error;

			const waitMs =
				delayMs ??
				backoffDelay(resolved.baseBackoffMs, resolved.maxBackoffMs, attempt);
			await sleep(waitMs, composedSignal);
		}

		// Exhausted attempts — last error already thrown above; safety net.
		throw new NetworkError("Max retry attempts exhausted");
	} finally {
		composed.cleanup();
	}
}

// Inc E: validate a single media item (shape + id format + safe URL) before it
// reaches an <img src>. The public viewer must not trust the backend blindly;
// full mirror of the studio client guard (incl. the hsm_ id regex, so cache
// signatures built from ids cannot collide on malformed ids).
const MEDIA_ID_REGEX = /^hsm_[0-9a-f]{16}$/;
function isValidHotspotMedia(value: unknown): value is HotspotMedia {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return false;
	}
	const m = value as Record<string, unknown>;
	return (
		typeof m["id"] === "string" &&
		MEDIA_ID_REGEX.test(m["id"] as string) &&
		isSafeHttpUrl(m["url"]) &&
		typeof m["mime"] === "string" &&
		typeof m["width_px"] === "number" &&
		Number.isFinite(m["width_px"]) &&
		typeof m["height_px"] === "number" &&
		Number.isFinite(m["height_px"]) &&
		(m["caption"] === null || typeof m["caption"] === "string") &&
		typeof m["order_index"] === "number" &&
		Number.isFinite(m["order_index"])
	);
}

export function createApiClient(config?: ApiClientConfig): ApiClient {
	const resolved = resolveConfig(config);

	const get = async <T>(path: string, opts?: ApiCallOptions): Promise<T> => {
		const url = `${resolved.baseUrl}${path}`;
		const payload = await performWithRetry(url, resolved, opts?.signal);
		return payload as T;
	};

	return {
		fetchModel(modelId, opts) {
			const variantQuery =
				opts?.variant !== undefined
					? `?variant=${encodeURIComponent(opts.variant)}`
					: "";
			const path = appendShareToken(
				`/api/model/${encodeURIComponent(modelId)}${variantQuery}`,
				resolved.shareToken,
			);
			return get<ModelMeta | ModelMetaVariant>(path, opts);
		},
		fetchModelManifest(modelId, opts) {
			return get<ModelManifest>(
				`/api/model/${encodeURIComponent(modelId)}/manifest`,
				opts,
			);
		},
		async fetchHotspots(modelId, opts) {
			// Inc E: normalize `media` to a safe array (default [] for legacy/invalid
			// responses) so the gallery never reads `.media` of undefined.
			const payload = await get<unknown>(
				appendShareToken(
					`/api/model/${encodeURIComponent(modelId)}/hotspots`,
					resolved.shareToken,
				),
				opts,
			);
			if (!Array.isArray(payload)) return [];
			return payload.map((raw) => {
				const media = (raw as { media?: unknown }).media;
				return {
					...(raw as Hotspot),
					media: Array.isArray(media) ? media.filter(isValidHotspotMedia) : [],
				};
			});
		},
		fetchPresets(modelId, opts) {
			return get<readonly Preset[]>(
				appendShareToken(
					`/api/model/${encodeURIComponent(modelId)}/presets`,
					resolved.shareToken,
				),
				opts,
			);
		},
		async fetchFloorPlan(modelId, opts) {
			try {
				// 5.D iter 4 fix (MAJOR): validate the response shape with URL
				// safety on image_url. Without this, a compromised/malformed
				// backend response with `image_url: 'javascript:alert(1)'` would
				// flow into the minimap <img src> and Studio thumbnail.
				const payload = await get<unknown>(
					appendShareToken(
						`/api/model/${encodeURIComponent(modelId)}/floorplan`,
						resolved.shareToken,
					),
					opts,
				);
				if (!isFloorPlan(payload)) {
					throw new ApiError({
						code: "internal_error",
						message: "fetchFloorPlan response missing required fields",
						httpStatus: 200,
						requestId: "",
						retryable: false,
					});
				}
				return payload;
			} catch (err) {
				if (err instanceof ApiError && err.code === "not_found") {
					return null;
				}
				throw err;
			}
		},
	};
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isVec2Finite(value: unknown): boolean {
	if (!Array.isArray(value) || value.length !== 2) return false;
	return value.every((n) => typeof n === "number" && Number.isFinite(n));
}

function isFloorPlan(value: unknown): value is FloorPlan {
	if (!isPlainRecord(value)) return false;
	const v = value as Record<string, unknown>;
	if (!isSafeHttpUrl(v["image_url"])) return false;
	if (typeof v["image_mime"] !== "string") return false;
	if (
		typeof v["image_width_px"] !== "number" ||
		!Number.isFinite(v["image_width_px"])
	)
		return false;
	if (
		typeof v["image_height_px"] !== "number" ||
		!Number.isFinite(v["image_height_px"])
	)
		return false;
	const cal = v["calibration"];
	if (!isPlainRecord(cal)) return false;
	if (
		!isVec2Finite(cal["world1"]) ||
		!isVec2Finite(cal["pixel1"]) ||
		!isVec2Finite(cal["world2"]) ||
		!isVec2Finite(cal["pixel2"])
	)
		return false;
	return v["source"] === "manual" || v["source"] === "auto_v1";
}
